/**
 * Generic PDF utilities — not tied to any specific document template.
 */
import { pdf } from '@react-pdf/renderer'
import type { ReactElement } from 'react'

/** Convert millimetres to react-pdf points. */
export const mm = (value: number) => value * 2.835

/** Render a react-pdf element to a blob and open it in a new browser tab. */
export async function previewPdf(element: ReactElement, filename: string): Promise<void> {
  await openPdf(() => pdf(element).toBlob(), filename)
}

/**
 * Open a PDF blob in a new browser tab.
 *
 * Opens the tab synchronously (before the blob is produced) so that the
 * browser treats it as a user-initiated popup — older Safari versions
 * block or download blob URLs when `window.open` is called after an await.
 * Falls back to a file download if the popup is still blocked.
 */
export async function openPdf(getBlob: () => Promise<Blob>, filename: string): Promise<void> {
  // Open the tab synchronously while the user gesture is still active
  const win = window.open('', '_blank')

  let blob: Blob
  try {
    blob = await getBlob()
  } catch (err) {
    win?.close()
    throw err
  }

  const url = URL.createObjectURL(blob)

  if (win) {
    win.location.href = url
    // Keep the blob URL alive long enough that the user can still hit the PDF
    // viewer's download button after reading for a while, but eventually free
    // the memory so previews don't accumulate over a long-lived SPA session.
    setTimeout(() => URL.revokeObjectURL(url), 30 * 60_000)
  } else {
    // Popup blocked — fall back to download
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }
}
